import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3" }
const _hoisted_3 = { class: "base-table-title mb-0" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "card mb-3"
}
const _hoisted_6 = { class: "card-header" }
const _hoisted_7 = { class: "mb-0" }
const _hoisted_8 = { class: "card-body" }
const _hoisted_9 = { class: "base-table-summary d-flex justify-content-between align-items-center text-muted small mb-2" }
const _hoisted_10 = {
  key: 0,
  class: "ms-2"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { class: "table-responsive base-table-wrap" }
const _hoisted_13 = { class: "table table-dark table-striped mb-0" }
const _hoisted_14 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_15 = { class: "pagination justify-content-center" }
const _hoisted_16 = ["onClick"]

import { computed } from 'vue'
import PanelNoAccess from '@/components/panel/PanelNoAccess.vue'

// Props
interface Props {
  title: string
  sectionId?: string
  itemName?: string
  paginatedData?: any[]
  totalItems?: number
  currentPage?: number
  itemsPerPage?: number
  loading?: boolean
  showFilters?: boolean
  showRefresh?: boolean
  filtersTitle?: string
  /** The account may not view this section: show the no-permission state instead of the table. */
  forbidden?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseTableComponent',
  props: {
    title: {},
    sectionId: { default: 'table-section' },
    itemName: { default: '' },
    paginatedData: { default: () => [] },
    totalItems: { default: 0 },
    currentPage: { default: 1 },
    itemsPerPage: { default: 25 },
    loading: { type: Boolean, default: false },
    showFilters: { type: Boolean, default: true },
    showRefresh: { type: Boolean, default: true },
    filtersTitle: { default: '' },
    forbidden: { type: Boolean, default: false }
  },
  emits: ["refresh", "page-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props

// Emits
const emit = __emit

// Computed
const totalPages = computed(() => {
  const total = Number(props.totalItems) || 0
  const perPage = Number(props.itemsPerPage) || 1
  return Math.max(1, Math.ceil(total / perPage))
})

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, props.currentPage - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    emit('page-change', page)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    id: __props.sectionId,
    class: "base-table mb-5"
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(__props.title), 1),
      _createElementVNode("div", null, [
        (!__props.forbidden)
          ? _renderSlot(_ctx.$slots, "header-actions", {}, () => [
              (__props.showRefresh)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn btn-primary",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('refresh'))),
                    disabled: __props.loading
                  }, [
                    _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fa fa-refresh me-1" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('panel.table.refresh')), 1)
                  ], 8, _hoisted_4))
                : _createCommentVNode("", true)
            ], undefined, 0)
          : _createCommentVNode("", true)
      ])
    ]),
    (__props.forbidden)
      ? (_openBlock(), _createBlock(PanelNoAccess, { key: 0 }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (__props.showFilters)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("h5", _hoisted_7, _toDisplayString(__props.filtersTitle || _ctx.$t('panel.table.filters')), 1)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _renderSlot(_ctx.$slots, "filters")
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            _renderSlot(_ctx.$slots, "summary", { totalPages: totalPages.value }, () => [
              _createElementVNode("span", null, [
                _createTextVNode(_toDisplayString(_ctx.$t('panel.table.showing', { shown: __props.paginatedData ? __props.paginatedData.length : 0, total: __props.totalItems, item: __props.itemName })) + " ", 1),
                (__props.loading)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                      _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(_ctx.$t('panel.table.loading')), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (totalPages.value > 1)
                ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_ctx.$t('panel.table.page', { page: __props.currentPage, pages: totalPages.value })), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("table", _hoisted_13, [
              _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _renderSlot(_ctx.$slots, "table-header")
                ])
              ]),
              _createElementVNode("tbody", null, [
                _renderSlot(_ctx.$slots, "table-body", {
                  data: __props.paginatedData,
                  loading: __props.loading
                })
              ])
            ])
          ]),
          (totalPages.value > 1)
            ? (_openBlock(), _createElementBlock("nav", _hoisted_14, [
                _createElementVNode("ul", _hoisted_15, [
                  _createElementVNode("li", {
                    class: _normalizeClass(["page-item", { disabled: __props.currentPage === 1 }])
                  }, [
                    _createElementVNode("a", {
                      class: "page-link",
                      href: "#",
                      onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (goToPage(__props.currentPage - 1)), ["prevent"]))
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("i", { class: "fa fa-chevron-left" }, null, -1)
                    ]))])
                  ], 2),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (page) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: page,
                      class: _normalizeClass(["page-item", { active: page === __props.currentPage }])
                    }, [
                      _createElementVNode("a", {
                        class: "page-link",
                        href: "#",
                        onClick: _withModifiers(($event: any) => (goToPage(page)), ["prevent"])
                      }, _toDisplayString(page), 9, _hoisted_16)
                    ], 2))
                  }), 128)),
                  _createElementVNode("li", {
                    class: _normalizeClass(["page-item", { disabled: __props.currentPage === totalPages.value }])
                  }, [
                    _createElementVNode("a", {
                      class: "page-link",
                      href: "#",
                      onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (goToPage(__props.currentPage + 1)), ["prevent"]))
                    }, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("i", { class: "fa fa-chevron-right" }, null, -1)
                    ]))])
                  ], 2)
                ])
              ]))
            : _createCommentVNode("", true)
        ], 64))
  ], 8, _hoisted_1))
}
}

})